import React, { useState, useEffect, useMemo } from 'react';
import { X, GitBranch, MessageSquare, ChevronRight, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useTheme } from '../contexts/ThemeContext';
import { applyAlpha } from '../lib/colorUtils';

/**
 * BranchTreeModal - Visual tree diagram showing conversation branches
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.branchTree - The branch tree data from API
 * @param {boolean} props.loading - Whether tree is being loaded
 * @param {string} props.activeConversationId - Currently active conversation ID
 * @param {Function} props.onSelectBranch - Callback when user selects a branch
 * @param {Object} props.brandVisualTheme - Brand visual theme for styling
 */
const BranchTreeModal = ({
  open,
  onClose,
  branchTree,
  loading,
  activeConversationId,
  onSelectBranch,
  brandVisualTheme,
}) => {
  const { resolvedTheme } = useTheme();
  const isBrandTheme = resolvedTheme === 'noderiver';
  const isDark = resolvedTheme === 'dark';
  
  const palette = brandVisualTheme?.palette || {};
  const primaryColor = palette.primary || '#1E8997';
  const accentGradient = brandVisualTheme?.gradients?.cta || 'linear-gradient(135deg, #1E8997, #0e273a)';
  
  const themeMode = brandVisualTheme?.modes?.[resolvedTheme] ?? brandVisualTheme?.modes?.light;
  const textColor = themeMode?.text ?? (isDark ? '#ffffff' : '#0f172a');
  const mutedColor = themeMode?.muted ?? (isDark ? '#94a3b8' : '#6b7280');
  const surfaceColor = themeMode?.surface ?? (isDark ? '#1e293b' : '#f8fafc');
  const borderColor = themeMode?.border ?? (isDark ? '#334155' : '#e2e8f0');

  // Build a hierarchical tree structure from flat branch data
  const treeNodes = useMemo(() => {
    if (!branchTree?.nodes) return [];
    
    const nodes = branchTree.nodes;
    const nodeMap = new Map();
    
    // First pass: create all nodes
    nodes.forEach(node => {
      nodeMap.set(node.id, {
        ...node,
        children: [],
      });
    });
    
    // Second pass: build parent-child relationships
    const rootNodes = [];
    nodes.forEach(node => {
      const mappedNode = nodeMap.get(node.id);
      if (node.parent_conversation_id && nodeMap.has(node.parent_conversation_id)) {
        nodeMap.get(node.parent_conversation_id).children.push(mappedNode);
      } else {
        rootNodes.push(mappedNode);
      }
    });
    
    return rootNodes;
  }, [branchTree]);

  const handleSelectBranch = (conversationId) => {
    onSelectBranch?.(conversationId);
    onClose?.();
  };

  // Recursive component to render tree nodes
  const TreeNode = ({ node, depth = 0, isLast = true, parentConnectors = [] }) => {
    const isActive = node.id === activeConversationId;
    const hasChildren = node.children && node.children.length > 0;
    const messageCount = node.message_count ?? node.messageCount ?? 0;
    const summary = node.branch_summary ?? node.summary ?? node.title ?? 'Conversation';
    const truncatedSummary = summary.length > 50 ? `${summary.slice(0, 50)}...` : summary;
    const branchPoint = node.branch_from_message_id ? `Branched from message` : 'Root';
    
    return (
      <div className="relative">
        {/* Connection lines */}
        <div className="absolute left-0 top-0 bottom-0 flex">
          {parentConnectors.map((showLine, idx) => (
            <div
              key={idx}
              className="w-6 flex-shrink-0 relative"
            >
              {showLine && (
                <div
                  className="absolute left-3 top-0 bottom-0 w-px"
                  style={{ backgroundColor: applyAlpha(primaryColor, 0.3) }}
                />
              )}
            </div>
          ))}
        </div>
        
        {/* Node content */}
        <div 
          className="flex items-start gap-2 relative"
          style={{ paddingLeft: `${depth * 24}px` }}
        >
          {/* Branch connector */}
          {depth > 0 && (
            <div className="absolute flex items-center" style={{ left: `${(depth - 1) * 24 + 12}px`, top: '16px' }}>
              <div 
                className="w-3 h-px"
                style={{ backgroundColor: applyAlpha(primaryColor, 0.3) }}
              />
              <div 
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: isActive ? primaryColor : applyAlpha(primaryColor, 0.5) }}
              />
            </div>
          )}
          
          {/* Node box */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleSelectBranch(node.id)}
                  className={`
                    flex-1 flex flex-col gap-1 p-3 rounded-xl border-2 transition-all text-left
                    hover:scale-[1.02] hover:shadow-lg
                    ${isActive 
                      ? 'ring-2 ring-offset-2' 
                      : 'hover:border-opacity-60'
                    }
                  `}
                  style={{
                    backgroundColor: isActive 
                      ? applyAlpha(primaryColor, 0.15) 
                      : surfaceColor,
                    borderColor: isActive 
                      ? primaryColor 
                      : borderColor,
                    boxShadow: isActive 
                      ? `0 0 20px ${applyAlpha(primaryColor, 0.25)}` 
                      : 'none',
                    '--tw-ring-color': primaryColor,
                    '--tw-ring-offset-color': surfaceColor,
                  }}
                >
                  {/* Header with icon and branch info */}
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ 
                        background: isActive ? accentGradient : applyAlpha(primaryColor, 0.1)
                      }}
                    >
                      {depth === 0 
                        ? <MessageSquare className="w-3 h-3" style={{ color: isActive ? '#fff' : primaryColor }} />
                        : <GitBranch className="w-3 h-3" style={{ color: isActive ? '#fff' : primaryColor }} />
                      }
                    </div>
                    <span 
                      className="text-xs font-medium"
                      style={{ color: mutedColor }}
                    >
                      {depth === 0 ? 'Root' : `Branch ${node.branch_depth ?? depth}`}
                    </span>
                    {isActive && (
                      <span 
                        className="text-[10px] px-1.5 py-0.5 rounded-full font-medium ml-auto"
                        style={{ 
                          backgroundColor: applyAlpha(primaryColor, 0.2),
                          color: primaryColor
                        }}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  
                  {/* Summary */}
                  <p 
                    className="text-sm font-medium line-clamp-2"
                    style={{ color: textColor }}
                  >
                    {truncatedSummary}
                  </p>
                  
                  {/* Meta info */}
                  <div className="flex items-center gap-3 text-xs" style={{ color: mutedColor }}>
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {messageCount} messages
                    </span>
                    {hasChildren && (
                      <span className="flex items-center gap-1">
                        <GitBranch className="w-3 h-3" />
                        {node.children.length} branch{node.children.length > 1 ? 'es' : ''}
                      </span>
                    )}
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent 
                side="right" 
                className="max-w-xs p-3"
                style={{ 
                  backgroundColor: isDark ? '#1e293b' : '#ffffff',
                  border: `1px solid ${borderColor}`,
                  color: textColor
                }}
              >
                <div className="space-y-2">
                  <p className="font-medium text-sm">{summary}</p>
                  <p className="text-xs" style={{ color: mutedColor }}>
                    {branchPoint} • {messageCount} messages
                  </p>
                  {node.created_at && (
                    <p className="text-xs" style={{ color: mutedColor }}>
                      Created: {new Date(node.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Render children */}
        {hasChildren && (
          <div className="mt-3 space-y-3">
            {node.children.map((child, idx) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                isLast={idx === node.children.length - 1}
                parentConnectors={[...parentConnectors, !isLast]}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Empty state when there are no branches
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div 
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
        style={{ backgroundColor: applyAlpha(primaryColor, 0.1) }}
      >
        <GitBranch className="w-8 h-8" style={{ color: primaryColor }} />
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: textColor }}>
        No branches yet
      </h3>
      <p className="text-sm max-w-xs" style={{ color: mutedColor }}>
        Create a branch from any message to explore different conversation paths.
        Click the branch icon below any message to get started.
      </p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent 
        className="max-w-2xl max-h-[80vh] p-0 overflow-hidden"
        style={{ 
          backgroundColor: isBrandTheme 
            ? 'rgba(15, 23, 42, 0.95)' 
            : (isDark ? '#0f172a' : '#ffffff'),
          borderColor: borderColor,
        }}
      >
        {/* Header */}
        <DialogHeader 
          className="px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: accentGradient }}
              >
                <GitBranch className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle 
                  className="text-lg font-semibold"
                  style={{ color: textColor }}
                >
                  Conversation Branches
                </DialogTitle>
                <p className="text-sm" style={{ color: mutedColor }}>
                  Navigate between different conversation paths
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <ScrollArea className="flex-1 max-h-[60vh]">
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 
                  className="w-8 h-8 animate-spin mb-4" 
                  style={{ color: primaryColor }} 
                />
                <p className="text-sm" style={{ color: mutedColor }}>
                  Loading branch tree...
                </p>
              </div>
            ) : treeNodes.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-3">
                {treeNodes.map((node, idx) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    isLast={idx === treeNodes.length - 1}
                    parentConnectors={[]}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer with branch count */}
        {!loading && treeNodes.length > 0 && (
          <div 
            className="px-6 py-3 border-t flex items-center justify-between"
            style={{ borderColor, backgroundColor: surfaceColor }}
          >
            <span className="text-sm" style={{ color: mutedColor }}>
              {branchTree?.totalBranches ?? treeNodes.length} total conversation{treeNodes.length !== 1 ? 's' : ''} in this tree
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-sm"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BranchTreeModal;

